import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { EditIcon, RangeGraph, useMinimalsTheme } from '@tom-ui/ui'
import { FC } from 'react'

type Props = {
  indicators: CarrierVisitOverviewCardDirectionItemProps[]
}

export type CarrierVisitOverviewCardDirectionItemProps = {
  key: string
  label: string
  containerCount: number
  globalRuleMatchCount: number
  visitRuleMatchCount: number
  unallocatedCount: number
  onEdit?: () => void
}

export const CarrierVisitOverviewCardDirections: FC<Props> = props => {
  const { indicators } = props
  const theme = useMinimalsTheme()

  return (
    <Box
      display='flex'
      flexDirection='column'
      bgcolor={theme.palette.grey[300]}
      gap='1px'
      pt='1px'
      pb='1px'
    >
      {indicators.map(indicator => (
        <CarrierVisitOverviewCardDirectionItem {...indicator} key={indicator.key} />
      ))}
    </Box>
  )
}

export const CarrierVisitOverviewCardDirectionItem: FC<
  CarrierVisitOverviewCardDirectionItemProps
> = props => {
  const {
    label,
    containerCount,
    globalRuleMatchCount,
    visitRuleMatchCount,
    unallocatedCount,
    onEdit,
  } = props

  const { t } = useTranslate()
  const theme = useMinimalsTheme()

  return (
    <Box
      display='flex'
      alignItems='center'
      gap={theme.customSpacing.s}
      bgcolor={theme.palette.grey[200]}
      pt={theme.customSpacing.xxs}
      pb={theme.customSpacing.xxs}
      pl={theme.customSpacing.s}
      pr={theme.customSpacing.s}
    >
      <Stack flex={2}>
        <Typography variant='caption' fontWeight={500}>
          {t(label)}
        </Typography>
      </Stack>

      <Stack flex={4}>
        {containerCount > 0 ? (
          <Tooltip
            title={t('visitOverviewMatchCountTitle', 'Visit rules / Global rules / Unallocated')}
          >
            <span>
              <RangeGraph
                height={6}
                hideLabels
                options={[
                  {
                    value: visitRuleMatchCount,
                    color: theme.palette.primary.main,
                    textColor: 'white',
                    order: 0,
                  },
                  {
                    value: globalRuleMatchCount,
                    color: theme.palette.warning.main,
                    textColor: 'white',
                    order: 1,
                  },
                  {
                    value: unallocatedCount,
                    color: theme.palette.grey[400],
                    textColor: 'white',
                    order: 2,
                  },
                ]}
              />
              <Typography variant='caption' color={theme.palette.text.secondary}>
                {t('rulesMatch', 'Rules match')}: {visitRuleMatchCount}/{globalRuleMatchCount}/
                {unallocatedCount}
              </Typography>
            </span>
          </Tooltip>
        ) : (
          <Typography variant='caption' color={theme.palette.text.secondary}>
            {t('nothingPlanned', 'Nothing planned')}
          </Typography>
        )}
      </Stack>

      {onEdit && (
        <Stack flex={1}>
          <IconButton onClick={onEdit}>
            <EditIcon fontSize='small' />
          </IconButton>
        </Stack>
      )}
    </Box>
  )
}
